/**
 * Calcul du ratio largeur/hauteur d'un QUAD `mask_zone` — mirror exact de
 * la fonction Python `app.services.mockups.aspect_ratio.compute_mask_aspect_ratio`.
 *
 * Anti-drift : ce module ET son équivalent Python sont vérifiés côté tests
 * contre le fixture partagé `backend/tests/fixtures/aspect_ratio_cases.json`.
 * Toute divergence casse la CI Vitest ou Pytest selon le côté modifié.
 *
 * Usage : feedback live dans le wizard d'upload de template (étape mask_zone)
 * pour afficher en temps réel la liste des formats catalogue compatibles
 * (ratio ± `MASK_RATIO_TOLERANCE`). Bloque la soumission si zéro match.
 *
 * Algorithme : `(w_top + w_bot) / (h_left + h_right)` — ratio APPARENT du
 * QUAD projeté (≈ R × cos θ pour un rectangle 3D rotated de θ). Précis à
 * <2% pour les mockups <10° de perspective, dégrade au-delà. Documentation
 * complète dans le module Python.
 */

// Tolérance relative pour le matching ratio template ↔ ratio format catalogue.
// **DOIT** rester égale à `MASK_RATIO_TOLERANCE` côté Python (`aspect_ratio.py`).
// Le fixture partagé exporte la même valeur en métadonnée — un test compare
// les trois (BE Python, FE TS, fixture) à chaque CI run.
export const MASK_RATIO_TOLERANCE = 0.05

// Précision des floats persistés / exposés côté API. Aligné sur Python.
const RATIO_DECIMALS = 4

export type Point = [number, number]
export type MaskZone = [Point, Point, Point, Point]

function distance(a: Point, b: Point): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1])
}

function roundTo(value: number, decimals: number): number {
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

/**
 * Ratio largeur/hauteur d'un QUAD via (w_top+w_bot)/(h_left+h_right).
 *
 * `mask_zone` est attendu en convention TL → TR → BR → BL. Lance si la
 * somme des hauteurs vaut zéro (QUAD horizontalement plat — bloqué en amont
 * par `quadAreaRatio` ≥ 1%, mais défense en profondeur).
 */
export function computeMaskAspectRatio(maskZone: MaskZone | number[][]): number {
  if (!Array.isArray(maskZone) || maskZone.length !== 4) {
    throw new Error('mask_zone doit contenir exactement 4 points')
  }
  const [tl, tr, br, bl] = maskZone as [Point, Point, Point, Point]

  const wTop = distance(tl, tr)
  const wBot = distance(bl, br)
  const hLeft = distance(tl, bl)
  const hRight = distance(tr, br)

  const heightSum = hLeft + hRight
  if (heightSum <= 0) {
    throw new Error(
      `mask_zone produit une hauteur nulle (h_left=${hLeft}, h_right=${hRight}) — QUAD dégénéré`,
    )
  }

  return roundTo((wTop + wBot) / heightSum, RATIO_DECIMALS)
}

/**
 * Aire d'un QUAD (formule du lacet) divisée par l'aire image. Sert à valider
 * que la zone n'est pas dégénérée. Retourne 0 pour un QUAD plat (colinéaire).
 */
export function quadAreaRatio(
  maskZone: MaskZone | number[][],
  imageWidth: number,
  imageHeight: number,
): number {
  if (imageWidth <= 0 || imageHeight <= 0) {
    throw new Error(`image dimensions invalides : ${imageWidth}×${imageHeight}`)
  }
  let crossSum = 0
  const n = maskZone.length
  for (let i = 0; i < n; i += 1) {
    const [xi, yi] = maskZone[i] as Point
    const [xj, yj] = maskZone[(i + 1) % n] as Point
    crossSum += xi * yj - xj * yi
  }
  return Math.abs(crossSum) / 2 / (imageWidth * imageHeight)
}

/**
 * Bornes (min, max) symétriques autour de `target` à `MASK_RATIO_TOLERANCE`
 * relatif. Mirror de `aspect_ratio.ratio_bounds` Python.
 */
export function ratioBounds(target: number): [number, number] {
  if (target <= 0) {
    throw new Error(`target ratio doit être > 0 (reçu ${target})`)
  }
  const delta = target * MASK_RATIO_TOLERANCE
  return [roundTo(target - delta, RATIO_DECIMALS), roundTo(target + delta, RATIO_DECIMALS)]
}

export interface FormatLite {
  slug: string
  label_fr: string
  label_en?: string | null
  width_cm: number | string
  height_cm: number | string
  active?: boolean
}

export interface CompatibleFormatMatch {
  format: FormatLite
  ratio: number
  errorPct: number
}

/**
 * Filtre une liste de formats catalogue pour ne garder que ceux dont le ratio
 * largeur/hauteur est à `MASK_RATIO_TOLERANCE` près de `maskAspectRatio`.
 * Trie par erreur croissante (le plus proche en premier).
 */
export function findCompatibleFormats(
  maskAspectRatio: number,
  formats: FormatLite[],
): CompatibleFormatMatch[] {
  const matches: CompatibleFormatMatch[] = []
  for (const format of formats) {
    if (format.active === false) continue
    const w = Number(format.width_cm)
    const h = Number(format.height_cm)
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) continue
    const ratio = w / h
    const errorRel = Math.abs(maskAspectRatio - ratio) / ratio
    if (errorRel <= MASK_RATIO_TOLERANCE) {
      matches.push({ format, ratio, errorPct: errorRel * 100 })
    }
  }
  matches.sort((a, b) => a.errorPct - b.errorPct)
  return matches
}
